import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Table,
} from 'reactstrap';
import { listComplaintsRequest } from '../../requests/complaint.request';
import {
  ComplaintAPI,
  ComplaintCategory,
  ComplaintFiledBy,
  ComplaintStatus,
} from './complaint-api.type';

const PAGE_SIZE_OPTIONS = [50, 100, 150, 200];

const ComplaintIndex: React.FC = () => {
  const [complaints, setComplaints] = useState<ComplaintAPI.Complaint[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ComplaintAPI.ComplaintFilters>({
    status: undefined,
    reasonCategory: undefined,
    complaintFiledBy: undefined,
    isClosed: undefined,
    overdue: undefined,
    limit: 50,
    skip: 0,
  });

  const fetchComplaints = useCallback(async () => {
    try {
      setLoading(true);
      const response = await listComplaintsRequest(filters);
      setComplaints(response.data || []);
      setTotal(response.total || 0);
    } catch (err) {
      setError('Failed to fetch complaints');
      console.error('Error fetching complaints:', err);
      setComplaints([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchComplaints();
  }, [fetchComplaints]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value || undefined,
      skip: 0,
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      limit: 50,
      skip: 0,
    });
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = Number(e.target.value);
    setFilters((prev) => ({
      ...prev,
      limit: newLimit,
      skip: 0,
    }));
  };

  const currentPage = Math.floor((filters.skip || 0) / (filters.limit || 50)) + 1;
  const totalPages = Math.ceil(total / (filters.limit || 50));

  const goToPage = (page: number) => {
    setFilters((prev) => ({
      ...prev,
      skip: (page - 1) * (prev.limit || 50),
    }));
  };

  const getStatusBadgeColor = (status: ComplaintStatus): string => {
    switch (status) {
      case 'waiting_client':
        return 'warning';
      case 'waiting_customer_service':
        return 'info';
      case 'waiting_valuation':
        return 'primary';
      case 'waiting_complaint_handler':
        return 'secondary';
      case 'waiting_cc_manager':
        return 'danger';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'dark';
      default:
        return 'secondary';
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages: number[] = [];
    for (let i = 1; i <= totalPages; i += 1) {
      pages.push(i);
    }

    return (
      <Pagination className="mt-3 justify-content-center">
        <PaginationItem disabled={currentPage === 1}>
          <PaginationLink first onClick={() => goToPage(1)} />
        </PaginationItem>
        <PaginationItem disabled={currentPage === 1}>
          <PaginationLink previous onClick={() => goToPage(currentPage - 1)} />
        </PaginationItem>
        {pages.map((page) => (
          <PaginationItem key={page} active={page === currentPage}>
            <PaginationLink onClick={() => goToPage(page)}>{page}</PaginationLink>
          </PaginationItem>
        ))}
        <PaginationItem disabled={currentPage === totalPages}>
          <PaginationLink next onClick={() => goToPage(currentPage + 1)} />
        </PaginationItem>
        <PaginationItem disabled={currentPage === totalPages}>
          <PaginationLink last onClick={() => goToPage(totalPages)} />
        </PaginationItem>
      </Pagination>
    );
  };

  const renderFilters = () => (
    <Card className="mb-4">
      <CardBody>
        <Form>
          <Row>
            <Col md={3}>
              <FormGroup>
                <Label for="status">Status</Label>
                <Input
                  type="select"
                  name="status"
                  id="status"
                  value={filters.status || ''}
                  onChange={handleFilterChange}
                >
                  <option value="">All</option>
                  {Object.values(ComplaintStatus).map((status) => (
                    <option key={status} value={status}>
                      {status.replace(/_/g, ' ')}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="reasonCategory">Category</Label>
                <Input
                  type="select"
                  name="reasonCategory"
                  id="reasonCategory"
                  value={filters.reasonCategory || ''}
                  onChange={handleFilterChange}
                >
                  <option value="">All</option>
                  {Object.values(ComplaintCategory).map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="complaintFiledBy">Filed By</Label>
                <Input
                  type="select"
                  name="complaintFiledBy"
                  id="complaintFiledBy"
                  value={filters.complaintFiledBy || ''}
                  onChange={handleFilterChange}
                >
                  <option value="">All</option>
                  {Object.values(ComplaintFiledBy).map((filedBy) => (
                    <option key={filedBy} value={filedBy}>
                      {filedBy}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="pageSize">Per page</Label>
                <Input
                  type="select"
                  name="pageSize"
                  id="pageSize"
                  value={filters.limit || 50}
                  onChange={handlePageSizeChange}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <FormGroup check>
                <Label check>
                  <Input
                    type="checkbox"
                    name="isClosed"
                    checked={filters.isClosed || false}
                    onChange={handleFilterChange}
                  />
                  Show Closed
                </Label>
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup check>
                <Label check>
                  <Input
                    type="checkbox"
                    name="overdue"
                    checked={filters.overdue || false}
                    onChange={handleFilterChange}
                  />
                  Show Overdue Only
                </Label>
              </FormGroup>
            </Col>
            <Col md={3} />
            <Col md={3}>
              <div className="d-flex gap-2">
                <Button color="primary" onClick={fetchComplaints}>
                  Apply Filters
                </Button>
                <Button color="secondary" onClick={handleResetFilters}>
                  Reset
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </CardBody>
    </Card>
  );

  const renderContent = () => {
    if (loading) {
      return <div className="text-center">Loading complaints...</div>;
    }

    if (error) {
      return <div className="text-center text-danger">{error}</div>;
    }

    return (
      <>
        {renderFilters()}
        <Table responsive hover>
          <thead>
            <tr>
              <th>Complaint #</th>
              <th>Status</th>
              <th>Category</th>
              <th>Filed By</th>
              <th>Due Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!complaints || complaints.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center">
                  No complaints found
                </td>
              </tr>
            ) : (
              complaints.map((complaint) => (
                <tr key={complaint.id}>
                  <td>{complaint.complaintNumber}</td>
                  <td>
                    <Badge color={getStatusBadgeColor(complaint.status)}>{complaint.status}</Badge>
                  </td>
                  <td>{complaint.reasonCategory}</td>
                  <td>{complaint.complaintFiledBy}</td>
                  <td>{new Date(complaint.dueDate).toLocaleDateString()}</td>
                  <td>
                    <Button color="link" size="sm" href={`/complaints/${complaint.id}`}>
                      View
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        {renderPagination()}
      </>
    );
  };

  return (
    <Card>
      <CardHeader className="d-flex justify-content-between align-items-center">
        <h4 className="mb-0">Complaints</h4>
        <Button color="primary" href="/complaints/create">
          Create New Complaint
        </Button>
      </CardHeader>
      <CardBody>{renderContent()}</CardBody>
    </Card>
  );
};

export default ComplaintIndex;
